import React, { useEffect, useState, useContext, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import FadeLoader from "react-spinners/FadeLoader";
import axios from "axios";
import error from "../assets/error.png";
import success from "../assets/success.png";
import { api_url } from "../utils/config";
import { LanguageContext } from "../context/LanguageContext";

const ConfirmOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [message, setMessage] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showFullReceipt, setShowFullReceipt] = useState(false);
  const [showFinalSuccess, setShowFinalSuccess] = useState(false);
  const confirmedRef = useRef(false);
  const { t } = useContext(LanguageContext);
  const queryOrderId = new URLSearchParams(window.location.search).get(
    "orderId",
  );
  const orderIdToUse =
    orderId || queryOrderId || localStorage.getItem("orderId") || "";
  const [receiptReference, setReceiptReference] = useState("");

  useEffect(() => {
    // Chapa returns to /order/confirm?orderId=.... Ask the server for
    // Chapa's receipt reference (not our merchant tx_ref).
    const loadReceiptReference = async () => {
      if (receiptReference || !orderIdToUse) return;

      try {
        const { data } = await axios.get(
          `${api_url}/api/payment/chapa/receipt/${orderIdToUse}`,
        );
        if (data?.reference) {
          setReceiptReference(data.reference);
        }
      } catch (error) {
        console.error("Could not load Chapa receipt reference:", error);
      }
    };

    loadReceiptReference();
  }, [orderIdToUse, receiptReference]);

  useEffect(() => {
    const verifyPayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const txRef = urlParams.get("trx_ref") || urlParams.get("tx_ref");
      const status = urlParams.get("status");

      if (txRef) {
        // Extract orderId from tx_ref (format: chapa-timestamp-orderId)
        const extractedOrderId = txRef.split("-")[2];
        if (extractedOrderId) {
          localStorage.setItem("orderId", extractedOrderId);
        }

        // If Chapa returns with status=success or no status (which means success)
        // we consider the payment successful
        if (status === "success" || !status) {
          setMessage("succeeded");
        } else {
          // Only verify with API if status is not clearly success
          try {
            const { data } = await axios.get(
              `${api_url}/api/payment/chapa/verify/${txRef}`,
            );
            if (data.payment_status === "paid") {
              setMessage("succeeded");
            } else {
              setMessage("failed");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            // If verification fails but we have a tx_ref, assume success
            // (Chapa wouldn't redirect with tx_ref if payment failed)
            setMessage("succeeded");
          }
        }
      } else if (orderId) {
        // Direct order confirmation
        setMessage("succeeded");
      } else {
        // No payment information available
        const storedOrderId = localStorage.getItem("orderId");
        if (storedOrderId) {
          setMessage("succeeded");
        } else {
          setMessage("failed");
        }
      }
    };

    verifyPayment();
  }, [orderId]);

  const update_payment = async () => {
    const orderIdToConfirm = orderIdToUse;
    if (orderIdToConfirm) {
      try {
        await axios.get(`${api_url}/api/order/confirm/${orderIdToConfirm}`);
        localStorage.removeItem("orderId");
        setLoader(false);
      } catch (error) {
        console.log(error.response?.data);
        setLoader(false);
      }
    } else {
      setLoader(false);
    }
  };

  const handleDone = async () => {
    setLoader(true);
    await update_payment();
    setShowFinalSuccess(true);
    navigate("/dashboard/my-orders", { replace: true });
  };

  useEffect(() => {
    if (message === "succeeded") {
      if (!confirmedRef.current) {
        confirmedRef.current = true;
        update_payment();
      }
      setShowReceipt(true);
      setLoader(false);
    }
  }, [message]);

  useEffect(() => {
    if (showReceipt) {
      localStorage.setItem(
        "paymentReceipt",
        JSON.stringify({
          orderId: orderIdToUse,
          receiptReference,
          status: "paid",
        }),
      );
    }
  }, [showReceipt, orderIdToUse, receiptReference]);

  return (
    <div className="w-screen h-screen flex justify-center items-center flex-col gap-4 bg-slate-50 px-4">
      {message === "failed" || message === "processing" ? (
        <>
          <img src={error} alt={t("paymentFailed")} />
          <Link
            className="px-5 py-2 bg-green-500 rounded-sm text-white"
            to="/dashboard/my-orders"
          >
            {t("backToDashboard")}
          </Link>
        </>
      ) : message === "succeeded" && !showFinalSuccess ? (
        showReceipt ? (
          <div className="w-full max-w-md rounded-lg bg-white shadow-lg border border-slate-200 p-6 text-slate-700">
            <div className="text-center mb-4">
              <img
                src={success}
                alt={t("paymentSucceeded")}
                className="mx-auto h-20 w-20"
              />
              <h2 className="text-2xl font-semibold mt-3 text-slate-800">
                Payment Receipt
              </h2>
            </div>

            <div className="space-y-3 text-sm border-t border-b border-slate-200 py-4">
              <div className="flex justify-between">
                <span>Status</span>
                <span className="font-medium text-green-600">Paid</span>
              </div>
              <div className="flex justify-between">
                <span>Order ID</span>
                <span className="font-medium">{orderIdToUse || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span>Reference</span>
                <span className="font-medium">
                  {receiptReference || "Chapa"}
                </span>
              </div>
            </div>

            <div className="mt-4">
              {receiptReference ? (
                <a
                  href={`https://checkout.chapa.co/checkout/test-payment-receipt/${encodeURIComponent(receiptReference)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-full text-center text-sm font-medium text-indigo-600 border border-indigo-200 rounded-sm px-3 py-2"
                >
                  View Chapa receipt
                </a>
              ) : (
                <div className="w-full text-center text-sm font-medium text-gray-500 border border-gray-200 rounded-sm px-3 py-2">
                  Receipt link unavailable
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowFullReceipt((prev) => !prev)}
                className="mt-3 w-full text-left text-sm font-medium text-indigo-600 border border-indigo-200 rounded-sm px-3 py-2"
              >
                {showFullReceipt
                  ? "Hide receipt details"
                  : "View receipt details"}
              </button>

              {showFullReceipt && (
                <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
                  <p>
                    <span className="font-medium text-slate-700">
                      Transaction:
                    </span>{" "}
                    {receiptReference || "Chapa payment"}
                  </p>
                  <p>
                    <span className="font-medium text-slate-700">Order:</span>{" "}
                    {orderIdToUse || "-"}
                  </p>
                  <p>
                    <span className="font-medium text-slate-700">Status:</span>{" "}
                    Paid successfully
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={handleDone}
              className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white font-medium rounded-sm px-4 py-3"
            >
              Done
            </button>
          </div>
        ) : (
          <FadeLoader />
        )
      ) : message === "succeeded" && showFinalSuccess ? (
        <>
          <img src={success} alt={t("paymentSucceeded")} />
          <p className="text-xl font-semibold text-slate-700">
            Payment successful
          </p>
          <Link
            className="px-5 py-2 bg-green-500 rounded-sm text-white"
            to="/dashboard/my-orders"
          >
            {t("backToDashboard")}
          </Link>
        </>
      ) : (
        <FadeLoader />
      )}
    </div>
  );
};

export default ConfirmOrder;
